import { Minimize2 } from "lucide-react";
import { CSSProperties, ReactNode, RefObject } from "react";
import { Link } from "react-router";
import { PageSection, PageSectionAction, PageSectionBadge } from "nazare-ui/components/page-section";

import { LoadingBar } from "./loading-bar";
import { ScrollShadow, TABLE_HEADER_HEIGHT } from "./scroll-shadow";
import styles from "./styles/maximised-table-page.module.css";

export type MaximisedTablePageProps = {
  /** Card header title, e.g. "Runs" or "Recent runs". */
  title: string;
  /** Rendered as a pill badge next to `title` — e.g. `"1247 runs"` or
   * `"42 of 140 loaded"` while filtered. */
  count: ReactNode;
  /** Shared-element name pairing this page with its dashboard card — must
   * match the `viewTransitionName` set on that card's own root element. */
  viewTransitionName: string;
  /** Route the minimise button returns to, e.g. `"/workflows"`. */
  minimiseTo: string;
  /** From this page's own `useScrollEdges()` — the actual scrolling element. */
  tableWrapRef: RefObject<HTMLDivElement | null>;
  atTop: boolean;
  atBottom: boolean;
  /** Same convention as `notifications/index.tsx`'s own `showLoadingBar`
   * (see `<LoadingBar>`'s doc comment): a thin indeterminate bar along the
   * card's top edge, for "rows already on screen, a background refetch for
   * fresher/fuller data is in flight" — NOT the initial empty-state load
   * (that's `children`'s own skeleton rows). Optional — pages with nothing
   * to seed from (no `keepPreviousData`/`initialData` predecessor) just
   * omit it. */
  showLoadingBar?: boolean;
  /** The `<table>` (or other scrollable content) itself. */
  children: ReactNode;
};

/**
 * Shared shell for every "maximised" full-page table in this app — the
 * `.page > .card[viewTransitionName] > header + scroll-shadowed table wrap`
 * structure that `workflows/pages/runs.tsx` and `qa-automation/pages/
 * recent-runs.tsx` each independently duplicated (byte-for-byte in most
 * places) before this existed. Callers still own their OWN table markup
 * (columns differ per page) and their own secondary-header filter/action
 * row (published separately via `useSecondaryHeader`) — this component only
 * owns the card shell, the title/count/minimise header, and the scroll-
 * shadowed scrolling container around whatever `children` renders.
 *
 * The title+pill-count header (as opposed to a bare count with no title)
 * is QA Automation's original pattern, kept here as canonical — workflows'
 * pages previously had no in-card title at all, relying solely on the
 * shell's breadcrumb for the page name; this unifies on the more legible
 * QA pattern for every consumer.
 */
export function MaximisedTablePage({
  title,
  count,
  viewTransitionName,
  minimiseTo,
  tableWrapRef,
  atTop,
  atBottom,
  showLoadingBar = false,
  children,
}: MaximisedTablePageProps) {
  return (
    <div className={styles.page}>
      <PageSection
        className={styles.card}
        style={{ viewTransitionName } as unknown as CSSProperties}
        title={title}
        badges={count != null ? <PageSectionBadge>{count}</PageSectionBadge> : undefined}
        overlay={showLoadingBar && <LoadingBar />}
        actions={
          <PageSectionAction asChild aria-label="Minimise to dashboard" title="Minimise to dashboard">
            <Link to={minimiseTo} viewTransition><Minimize2 aria-hidden="true" /></Link>
          </PageSectionAction>
        }
      >
        <div className={styles.scrollShadowWrapper}>
          <div ref={tableWrapRef} className={styles.tableWrap}>
            {children}
          </div>
          <ScrollShadow atTop={atTop} atBottom={atBottom} topOffset={TABLE_HEADER_HEIGHT} />
        </div>
      </PageSection>
    </div>
  );
}
